/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState } from 'react';

import {
    Button,
    ButtonGroup,
    FlexCol,
    FlexRow,
    FormattedDateTime,
    Heading,
    Icon,
    ModalActions,
    SecurityHelper,
    StyledModal,
    TextArea,
} from 'sarsaparilla';

import type { PublicReviewType } from '../model/publicReview';
import { describeCoverage } from '../utils/cellCoverage';
import CellCoverageIcon from './CellCoverageIcon';
import StarDisplay from './StarDisplay';

type PublicReviewProps = {
    review: PublicReviewType;
    flagReview: (params: {
        id?: string;
        reservation_id?: string;
        reason: string;
    }) => void;
    voteReview: (params: {
        id?: string;
        reservation_id?: string;
        is_helpful: boolean;
        vote_changed: boolean;
    }) => void;
    openLoginModal: () => void;
};

export default function PublicReview({
    review,
    flagReview,
    voteReview,
    openLoginModal,
}: PublicReviewProps) {
    const reviewKey = review.id || review.reservation_id;

    const [currentVote, setCurrentVote] = useState<boolean | null>(() => {
        const stored = localStorage.getItem(reviewKey);
        if (stored === 'true') return true;
        if (stored === 'false') return false;
        return null;
    });
    const [flagFeedback, setFlagFeedback] = useState<string>('');
    const [helpfulVotesCount, setHelpfulVotesCount] = useState<number>(
        review.helpful_votes_count
    );
    const [notHelpfulVotesCount, setNotHelpfulVotesCount] = useState<number>(
        review.not_helpful_votes_count
    );
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showMore, setShowMore] = useState<boolean>(false);

    const summary =
        review.review.length > 200 ? `${review.review.substring(0, 200)}...` : '';

    const toggleShowMore = () => {
        setShowMore(!showMore);
    };

    const toggleShowModal = () => {
        if (!SecurityHelper.isLoggedIn()) {
            openLoginModal();
            return;
        }

        setShowModal(!showModal);
    };

    const reviewText = () => {
        if (!summary || showMore) {
            return review.review;
        }
        return summary;
    };

    const handleVote = (is_helpful: boolean) => {
        if (!SecurityHelper.isLoggedIn()) {
            openLoginModal();
            return;
        }

        const identifier = review.id
            ? { id: review.id }
            : { reservation_id: review.reservation_id };
        const vote_changed = currentVote !== null;
        let newHelpfulVotesCounts = helpfulVotesCount;
        let newNotHelpfulVotesCounts = notHelpfulVotesCount;

        if (is_helpful) {
            newHelpfulVotesCounts += 1;
            if (vote_changed) {
                newNotHelpfulVotesCounts -= 1;
            }
        } else {
            newNotHelpfulVotesCounts += 1;
            if (vote_changed) {
                newHelpfulVotesCounts -= 1;
            }
        }

        setHelpfulVotesCount(newHelpfulVotesCounts);
        setNotHelpfulVotesCount(newNotHelpfulVotesCounts);
        setCurrentVote(is_helpful);
        voteReview({ ...identifier, is_helpful, vote_changed });
    };

    const handleFlag = () => {
        const identifier = review.id
            ? { id: review.id }
            : { reservation_id: review.reservation_id };
        flagReview({ ...identifier, reason: flagFeedback });
        toggleShowModal();
    };

    const renderDescription = () => {
        const resInfo = review.reservation_info ? review.reservation_info : ({} as any);
        switch (review.location_type) {
            case 'Campground':
            case 'Campsite':
                if (!resInfo?.product_name || !resInfo?.product_description) {
                    return null;
                }
                return (
                    <div>
                        <strong>Loop: </strong> {resInfo?.product_name},
                        <strong> Site: </strong> {resInfo?.product_description}
                    </div>
                );
            case 'Tour':
                return (
                    <div>
                        <strong>Tour Name: </strong> {resInfo?.facility_description}
                    </div>
                );
            case 'Permit':
                return (
                    <div>
                        <strong>Zone: </strong> {resInfo?.facility_name}
                    </div>
                );
            default:
                return null;
        }
    };

    const renderDates = () => {
        const resInfo = review.reservation_info ? review.reservation_info : ({} as any);

        switch (review.location_type) {
            case 'Tour':
                return (
                    <div>
                        <strong>Tour Date: </strong>{' '}
                        {resInfo?.start_date ? (
                            <FormattedDateTime
                                date={new Date(resInfo.start_date)}
                                timeZone="UTC"
                            />
                        ) : null}
                    </div>
                );
            case 'Campground':
            case 'Permit':
            default:
                return (
                    <div>
                        <strong>Reservation Dates: </strong>{' '}
                        {resInfo?.start_date ? (
                            <FormattedDateTime
                                date={new Date(resInfo.start_date)}
                                timeZone="UTC"
                            />
                        ) : null}
                        {resInfo?.end_date ? (
                            <span>
                                &nbsp;–&nbsp;
                                <FormattedDateTime
                                    date={new Date(resInfo.end_date)}
                                    timeZone="UTC"
                                />
                            </span>
                        ) : null}
                    </div>
                );
        }
    };

    return (
        <div className="public-review-wrapper">
            <StarDisplay value={review.rating} />
            <FlexRow
                justifyContent="space-between"
                alignItems="center"
                hasGutters={false}
                className="heading-row"
            >
                <FlexCol xs={12} md={4}>
                    <Heading
                        headingLevel={3}
                        appearance="h5"
                        className="mb-0"
                        data-notranslate
                        aria-label={`${review.rating} stars - ${review.first_name}`}
                    >
                        {review.first_name} {review.last_initial}
                    </Heading>
                </FlexCol>
                <FlexCol xs={12} md="variable" className="submitted-by">
                    Submitted on{' '}
                    <FormattedDateTime
                        date={new Date(review.created_at)}
                        timeZone="UTC"
                    />
                </FlexCol>
            </FlexRow>
            {renderDescription()}
            {renderDates()}
            <p className="mt-2" data-notranslate>
                {reviewText()}
            </p>
            {!!summary &&
                (showMore ? (
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="chevron-up" />}
                        onClick={toggleShowMore}
                        className="show-more-toggle"
                        gaTrackingId="304665831973"
                    >
                        Show Less
                    </Button>
                ) : (
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="chevron-down" />}
                        onClick={toggleShowMore}
                        className="show-more-toggle"
                        gaTrackingId="304665831973"
                    >
                        Show More
                    </Button>
                ))}
            {review.cell_coverage_ratings &&
                review.cell_coverage_ratings.map((row, i) => (
                    <FlexRow
                        className="cell-row-wrap"
                        justifyContent="space-around"
                        alignItems="center"
                        key={`cell-rating-${i}`}
                    >
                        <FlexCol xs={12} md={5} lg={12} xl={5} className="cell-row">
                            <Icon iconName="cell-carrier" size="sm" />{' '}
                            <strong>Phone Carrier: </strong> {row.carrier}
                        </FlexCol>
                        <FlexCol xs={12} md={7} lg={12} xl={7} className="cell-row">
                            <CellCoverageIcon
                                value={parseInt(row.rating, 10)}
                                size="sm"
                            />{' '}
                            <strong>Coverage Strength: </strong> {`(${row.rating}) `}
                            {` ${describeCoverage(row.rating)}`}
                        </FlexCol>
                    </FlexRow>
                ))}
            <ButtonGroup isFullWidthOnMobile={false}>
                <i className="mr-2">Was this review helpful?</i>
                <Button
                    appearance="tertiary"
                    size="xs"
                    isDisabled={currentVote === true}
                    onClick={() => handleVote(true)}
                    screenReaderTextBefore="Vote review helpful"
                    aria-label={`Yes, ${helpfulVotesCount} people`}
                    gaTrackingId="304665831973"
                >
                    {`Yes (${helpfulVotesCount})`}
                </Button>
                <Button
                    appearance="tertiary"
                    size="xs"
                    isDisabled={currentVote === false}
                    onClick={() => handleVote(false)}
                    screenReaderTextBefore="Vote review unhelpful"
                    aria-label={`No, ${notHelpfulVotesCount} people`}
                    gaTrackingId="304665831973"
                >
                    {`No (${notHelpfulVotesCount})`}
                </Button>
                <Button
                    id="flag-review-button-createAt"
                    appearance="tertiary"
                    size="xs"
                    isDisabled={review.moderator_approved}
                    onClick={toggleShowModal}
                    iconBeforeElement={<Icon iconName="flag" />}
                    aria-label="Flag review"
                    gaTrackingId="304665831973"
                />
            </ButtonGroup>
            {showModal && (
                <div id="complementary">
                    <StyledModal
                        onRequestClose={toggleShowModal}
                        isOpen={showModal}
                        shouldShowCloseButton
                    >
                        <div className="rec-modal-content-wrap">
                            <TextArea
                                placeholder="Enter feedback here"
                                label="Tell us why you find this review inappropriate"
                                isRequired
                                errorText="Max character count: 300"
                                value={flagFeedback}
                                onChange={(e: {
                                    target: { value: React.SetStateAction<string> };
                                }) => setFlagFeedback(e.target.value)}
                                id="flag-reason-input"
                            />
                        </div>
                        <ModalActions>
                            <Button
                                appearance="primary"
                                isDisabled={flagFeedback.length < 1}
                                onClick={handleFlag}
                                gaTrackingId="304665831973"
                            >
                                Submit
                            </Button>
                        </ModalActions>
                    </StyledModal>
                </div>
            )}
        </div>
    );
}
